import { Injectable } from '@angular/core';
import {
  ApplicantAppEntityApplicantRead,
  ApplicantService,
  ApplicationAppEntityApplicationRead,
  ApplicationAttachmentService,
  ApplicationService,
  AttachmentAttachmentRead,
  PatchApplicantApplicantItemRequestParams,
  ContractorService,
  ContractorAppEntityContractorRead,
  ApplicationAppEntityApplicationWrite,
  ApplicationAttachmentAppEntityApplicationRead,
  ServiceAttachmentPositionBaseReadServiceAttachmentPositionItemRead,
  ContractorAppEntityContractorWrite,
  ParcelAppEntityParcelWrite,
  ParcelService
} from '../openapi';
import { concatMap, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { dateToPLString } from './helpers';
import { ApplicationListItem, ApplicationPayment, AttachmentPositions } from './application.common.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  ContractorTag
} from '../containers/applications/application/common/contractor-data-section/contractor-data-section.component';

export type ApplicationCode = 'roboczy' | 'zalatwiony' | 'wyslany' | 'zatwierdzony';

export interface PorIdApplicationItem {
  porId: string;
  description: string;
  projectType: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationCommonService {
  constructor(
    private applicationService: ApplicationService,
    private applicantService: ApplicantService,
    private applicationAttachmentService: ApplicationAttachmentService,
    private contractorService: ContractorService,
    private parcelService: ParcelService,
    private http: HttpClient
  ) {
  }

  public updateLocalisationData(
    application: ApplicationAppEntityApplicationRead,
    payload: ApplicationAppEntityApplicationWrite,
    parcels: ParcelAppEntityParcelWrite[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = application.parcels?.map(v => this.parcelService.deleteParcelParcelItem({ id: v?.uuid || '' })) || [];

    const parcelsPostRequests: Observable<any>[] = parcels.map(parcel =>
      this.parcelService.postParcelParcelCollection({
        parcel
      }).pipe(map(v => v.uuid || ''))
    );
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() =>
        (parcelsPostRequests.length ? forkJoin(parcelsPostRequests) : of([])).pipe(
          switchMap(parcelIds =>
            this.applicationService.patchApplicationApplicationItem({
              id: application.uuid || '',
              application: {
                ...payload,
                parcels: parcelIds
              }
            })
          )
        )
      )
    );
  }

  public loadAttachmentPositionsByApplicationId(applicationId: string): Observable<AttachmentPositions> {
    return this.http.get<ServiceAttachmentPositionBaseReadServiceAttachmentPositionItemRead[]>(`${environment.apiBasePath}/service-attachments-positions/by-application-id/${applicationId}?pagination=false`)
      .pipe(
        map(items => items.map(item => {
          return {
            tag: item?.tag || '',
            description: item?.name || '',
            required: !!item?.isRequired,
            multiple: !!item?.isMultiple,
            payment: item?.fee
          };
        }) || [])
      );
  }
  public createApplication(serviceTypeId: string, applicants: PatchApplicantApplicantItemRequestParams[], application?: ApplicationAppEntityApplicationWrite): Observable<ApplicationAppEntityApplicationRead> {
    return forkJoin(applicants.map(v => v.applicant).map(applicant => this.applicantService.postApplicantApplicantCollection({ applicant }))).pipe(
      switchMap(applicants =>
        this.applicationService.postApplicationApplicationCollection({
          application: {
            ...application,
            service: serviceTypeId,
            applicants: applicants.map(applicant => applicant?.uuid || '')
          }
        })
      )
    );
  }

  public updateApplicants(applicationId: string, applicants: PatchApplicantApplicantItemRequestParams[], application?: ApplicationAppEntityApplicationWrite): Observable<ApplicationAppEntityApplicationRead> {
    // for existing
    const applicantPatchRequests: Observable<ApplicantAppEntityApplicantRead>[] = applicants.filter(v => !!v.id).map(item => this.applicantService.patchApplicantApplicantItem(item));

    // for new when id is empty string
    const applicantPostRequests: Observable<ApplicantAppEntityApplicantRead>[] = applicants.filter(v => !v.id).map(v => v.applicant).map(applicant => this.applicantService.postApplicantApplicantCollection({
      applicant
    }));
    return forkJoin([
      ...applicantPostRequests,
      ...applicantPatchRequests
    ]).pipe(
      switchMap((applicants) => this.applicationService.patchApplicationApplicationItem({
        id: applicationId,
        application: { ...application, applicants: applicants.map(applicant => applicant?.uuid || '') }
      }))
    );
  }

  public updateContractors(
    application: ApplicationAppEntityApplicationRead,
    payload: ApplicationAppEntityApplicationWrite,
    contractors: ContractorAppEntityContractorWrite[],
    tag: ContractorTag = 'investor'
  ): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests: Observable<any>[] = application?.contractors?.filter(v => v?.tag === tag)
      ?.map(v => this.contractorService.deleteContractorContractorItem({ id: v?.uuid || '' })) || [];
    if (contractors.length) {
      const postRequests: Observable<ContractorAppEntityContractorRead>[] = contractors.map(contractor => this.contractorService.postContractorContractorCollection({
        contractor: {
          ...contractor,
          application: application?.uuid,
          tag
        }
      }));
      return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
        switchMap(() => forkJoin(postRequests)),
        concatMap(() => this.applicationService.patchApplicationApplicationItem({
          id: application?.uuid || '', application: { ...payload }
        }))
      );
    } else {
      return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
        switchMap(() => this.applicationService.patchApplicationApplicationItem({
          id: application?.uuid || '', application: payload
        }))
      );
    }
  }

  public updateApplicationAttachments(
    application: ApplicationAppEntityApplicationRead, attachments: AttachmentAttachmentRead[]
  ): Observable<any> {
    if (!attachments.length) {
      return this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' });
    }
    const oldIds: string[] = application?.attachments?.map((v: ApplicationAttachmentAppEntityApplicationRead) => v.id || '') || [];
    if (oldIds?.length) {
      return forkJoin(oldIds.map(v => this.applicationAttachmentService.deleteApplicationAttachmentItemApplicationAttachmentItem({
        id: v
      }))).pipe(
        switchMap(() => forkJoin(attachments?.map(attachment => this.applicationAttachmentService.postApplicationAttachmentCollectionApplicationAttachmentCollection({
          applicationAttachment: {
            application: application?.uuid,
            attachment: attachment?.id
          }
        }))).pipe(
          switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
        ))
      );
    } else {
      return forkJoin(
        attachments?.map(attachment =>
          this.applicationAttachmentService.postApplicationAttachmentCollectionApplicationAttachmentCollection({
            applicationAttachment: {
              application: application?.uuid,
              attachment: attachment?.id
            }
          })
        )
      ).pipe(
        switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
      );
    }
  }

  public loadApplicationList(code: ApplicationCode): Observable<ApplicationListItem[]> {
    return this.applicationService.getApplicationCollectionApplicationCollection({
      pagination: false,
      statusCode: code,
      orderUpdatedAt: code === 'roboczy' ? 'desc' : undefined,
      orderApprovedAt: code === 'zatwierdzony' ? 'desc' : undefined,
      orderSentAt: code === 'wyslany' || code === 'zalatwiony' ? 'desc' : undefined
    }).pipe(
      map((applications: Array<ApplicationAppEntityApplicationRead>) => applications.filter(v => !!v?.service).map(v => ({
          tag: `${v?.service?.name || ''} ${dateToPLString(v?.createdAt) || ''} ${dateToPLString(v?.updatedAt) || ''} ${dateToPLString(v?.sentAt) || ''} ${dateToPLString(v?.approvedAt) || ''}`,
          application: v
        }))
      )
    );
  }

  public loadPorIdApplications(): Observable<PorIdApplicationItem[]> {
    return this.applicationService.getApplicationCollectionApplicationCollection({
      pagination: false,
      service: 'c308f70c-eddd-4e7d-928d-3a86261b7ae5'
    }).pipe(
      map(v =>
        v.filter(v => !!v?.applicationSubject && !!v?.porId)
          .map(v => ({
            porId: v.uuid || '',
            description: `${v.applicationSubject} - (${dateToPLString(v.updatedAt)})`,
            projectType: v?.projectType || ''
          }))
      )
    );
  }

  public loadApplicationPayments(applicationId: string): Observable<ApplicationPayment> {
    return this.http.post<ApplicationPayment>(`${environment.apiBasePath}/application/${applicationId}/cost-forecast`, {});
  }


  public signApplication(applicationId: string): Observable<{ result: string; redirect_url: string }> {
    return this.http.get<{
      result: string;
      redirect_url: string
    }>(`${environment.apiBasePath}/application/${applicationId}/sign-attachments`);
  }

}
